import api, { type ApiResponse } from '@/axios.js'
import { useServicesStore } from './services'
import { useOrdersStore } from './orders'
import { useUserStore } from './user'
import { defineStore } from 'pinia'
import { workingTimeError, type WorkingTimeConfirmation } from '@/api/workingTimeConflict'

export interface WorkingTimeInterface {
    id: number;
    date: string;
    start: string;
    end: string;
    person_id: number | null;
    date_human_text?: string;
    person_name?: string;
    option_id?: number;
}
export interface WorkingTimePeriod {
    person_id: number | null;
    date_from: string;
    date_to: string;
}
function invalidateAvailability(confirmation?: WorkingTimeConfirmation) {
    useServicesStore()._full_list = null
    if (confirmation?.cancel_conflicts) {
        const orders = useOrdersStore()
        orders._orders = null
        orders._my = null
        orders._all = null
        useUserStore()._list = null
    }
}
export const useWorkingTimeStore = defineStore('working_time', {
    state: () => ({ _list: null as WorkingTimeInterface[] | null }),
    getters: { list: state => state._list },
    actions: {
        async load(): Promise<void> {
            try {
                const response = await api.get<ApiResponse<WorkingTimeInterface[]>>('/working-time')
                this._list = response.data.data
            } catch (error) { throw workingTimeError(error) }
        },
        async destroy(id: number, confirmation?: WorkingTimeConfirmation): Promise<void> {
            try {
                await api.delete(`/working-time/${id}`, { data: confirmation })
                this._list = this._list?.filter(item => item.id !== id) ?? null
                invalidateAvailability(confirmation)
            } catch (error) { throw workingTimeError(error) }
        },
        async update(request: WorkingTimeInterface, confirmation?: WorkingTimeConfirmation): Promise<number> {
            try {
                const response = await api.put<ApiResponse<WorkingTimeInterface>>(`/working-time/${request.id || 0}`, { ...request, ...confirmation })
                this._list = [...(this._list || []).filter(item => item.id !== response.data.data.id), response.data.data]
                invalidateAvailability(confirmation)
                return response.data.data.id
            } catch (error) { throw workingTimeError(error) }
        },
        async delete(request: WorkingTimeInterface, confirmation?: WorkingTimeConfirmation): Promise<void> {
            await this.destroy(request.id, confirmation)
        },
        async removePeriod(period: WorkingTimePeriod, confirmation?: WorkingTimeConfirmation): Promise<void> {
            try {
                await api.post('/working-time/remove-period', { ...period, ...confirmation })
                this._list = this._list?.filter(item => !(Number(item.person_id) === Number(period.person_id) && item.date >= period.date_from && item.date <= period.date_to)) ?? null
                invalidateAvailability(confirmation)
            } catch (error) { throw workingTimeError(error) }
        },
    },
})
